import { useEffect, useRef, useCallback, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { LuArrowDownToLine } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { directoryExists, fileApiUrl, fetchUrl } from '@utils/api';
import { makePhotoLayer } from '@utils/map/layer/photo';
import { getLayerByCustomId } from '@utils/map/helpers';
import LayerSettings from '../sidebars/sidebarElements/LayerSettings';
import { downloadFile } from '@utils/files';

/*
 *  This component is responsible for rendering the photo layer and toggling its visibility.
 */
const PhotoLayer = ({
	name,
	layerId,
	directory,
	zIndex,
	visible = true,
	checkDirectory = false,
	downloadPath = null,
}) => {
	const { mapObject, defaultProjection, project, extent } = useProject();

	const adding = useRef(false);
	const [photoLayer, setPhotoLayer] = useState(null);
	const { title, uuid } = project;

	const projectDirectory = uuid + '/' + directory;

	const { data: checkDirectoryExists } = useQuery({
		queryKey: ['directoryExists', projectDirectory],
		queryFn: () => directoryExists(projectDirectory),
		enabled: !!projectDirectory && checkDirectory,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchOnMount: false,
		keepPreviousData: true,
	});

	const { mutateAsync: getSignedImage } = useMutation({
		mutationFn: () => fetchUrl(`fileLink?key=${uuid}/${downloadPath}`),
	});

	const download = async () => {
		try {
			const data = await getSignedImage();

			const imageUrl = data?.url;

			if (!imageUrl) {
				throw new Error('No image URL found');
			}
			const imageData = {
				url: imageUrl,
				title: `${title}_orthophoto`,
				fileExtension: 'tif',
			};

			downloadFile(imageData);
		} catch (error) {
			console.warn('Error downloading image:', error);
			window.alert('Image could not be downloaded. Please try again later.');
		}
	};

	const directoryDoesExists = !checkDirectory ? true : checkDirectoryExists;

	const updatePhotoLayer = useCallback(() => {
		if (!mapObject || !extent || adding.current || !directoryDoesExists) return null;

		adding.current = true;

		// Check if the layer already has been added to the map. It could be if this component is re-rendered.
		const existingLayer = getLayerByCustomId(mapObject, layerId);
		if (existingLayer) {
			console.log(`Layer ${name} already exists. Adding it to state.`);
			setPhotoLayer(existingLayer);
			adding.current = false;

			return;
		}

		const xyzUrl = fileApiUrl + '/filelink?key=' + projectDirectory + '/{z}/{x}/{-y}.png';

		const maxZoomLevel = project?.max_zoom_level ?? 21;

		const newLayer = makePhotoLayer({
			name,
			zIndex,
			defaultProjection,
			url: xyzUrl,
			extent,
			maxZoomLevel,
			visible,
			layerId,
		});

		if (newLayer) mapObject.addLayer(newLayer);
		setPhotoLayer(newLayer);

		adding.current = false;
	}, [mapObject, directoryDoesExists]);

	useEffect(() => {
		updatePhotoLayer();
	}, [updatePhotoLayer]);

	if (!photoLayer) return null;

	const layerFunctions = downloadPath
		? [
				{
					title: 'Download Photo',
					onClick: download,
					icon: <LuArrowDownToLine />,
				},
			]
		: null;

	return (
		<li>
			<LayerSettings
				id={layerId}
				layer={photoLayer}
				label={photoLayer.get('name')}
				defaultState={photoLayer.getVisible()}
				onCheck={() => photoLayer?.setVisible(true)}
				onUncheck={() => photoLayer?.setVisible(false)}
				disabled={!photoLayer}
				layerFunctions={layerFunctions}
			/>
		</li>
	);
};

export default PhotoLayer;
